/**
 * Read-only task panel for zero-config mode
 */

import React, { useState, useMemo } from 'react';
import type { ClientTask } from '../scanner';
import { VirtualList } from './VirtualList';

export interface TaskPanelProps {
  tasks: ClientTask[];
  isScanning?: boolean;
  lastScanTime?: Date | null;
  onRescan?: () => void;
  className?: string;
}

const TASK_TYPE_COLORS = {
  todo: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  fixme: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  bug: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  hack: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  note: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  optimize: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  refactor: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
};

const PRIORITY_ICONS = {
  high: '🔴',
  medium: '🟡',
  low: '🟢',
};

export function TaskPanel({
  tasks,
  isScanning = false,
  lastScanTime,
  onRescan,
  className = '',
}: TaskPanelProps) {
  const [filter, setFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Filter and search tasks
  const filteredTasks = useMemo(() => {
    return tasks.filter((task) => {
      // Type filter
      if (filter !== 'all' && task.type !== filter) {
        return false;
      }

      // Search filter
      if (searchTerm && !task.content.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }

      return true;
    });
  }, [tasks, filter, searchTerm]);

  // Count tasks by type
  const taskCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    tasks.forEach((task) => {
      counts[task.type] = (counts[task.type] || 0) + 1;
    });
    return counts;
  }, [tasks]);

  const taskTypes = Object.keys(taskCounts);

  return (
    <div className={`flex flex-col h-full bg-white dark:bg-gray-900 ${className}`}>
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Tasks ({filteredTasks.length})
          </h2>
          <button
            onClick={onRescan}
            disabled={isScanning}
            className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isScanning ? 'Scanning...' : 'Rescan'}
          </button>
        </div>

        {/* Search */}
        <div className="mb-3">
          <input
            type="text"
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 overflow-x-auto">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1 text-sm rounded whitespace-nowrap ${
              filter === 'all'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            All ({tasks.length})
          </button>
          {taskTypes.map((type) => (
            <button
              key={type}
              onClick={() => setFilter(type)}
              className={`px-3 py-1 text-sm rounded whitespace-nowrap ${
                filter === type
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {type.toUpperCase()} ({taskCounts[type]})
            </button>
          ))}
        </div>
      </div>

      {/* Task list */}
      <div className="flex-1 overflow-y-auto">
        {filteredTasks.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            {searchTerm || filter !== 'all'
              ? 'No tasks match your filters'
              : 'No tasks found. Tasks will appear here when detected in your code.'}
          </div>
        ) : (
          <VirtualList
            items={filteredTasks}
            height={600}
            itemHeight={120}
            overscan={3}
            renderItem={(task, index) => (
              <div className="px-4 py-2">
                <TaskCard task={task} />
              </div>
            )}
            className="w-full"
            emptyMessage="No tasks found. Tasks will appear here when detected in your code."
          />
        )}
      </div>

      {/* Footer */}
      {lastScanTime && (
        <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 px-4 py-2 text-xs text-gray-500 dark:text-gray-400">
          Last scan: {lastScanTime.toLocaleTimeString()}
        </div>
      )}
    </div>
  );
}

function TaskCard({ task }: { task: ClientTask }) {
  const typeColor = TASK_TYPE_COLORS[task.type as keyof typeof TASK_TYPE_COLORS] || TASK_TYPE_COLORS.note;
  const priorityIcon = PRIORITY_ICONS[task.priority];

  return (
    <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="flex items-start gap-3">
        <span className="text-lg" title={`Priority: ${task.priority}`}>
          {priorityIcon}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-2 py-0.5 text-xs font-medium rounded ${typeColor}`}>
              {task.type.toUpperCase()}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {task.file}
            </span>
          </div>
          <p className="text-sm text-gray-900 dark:text-white break-words">
            {task.content}
          </p>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Source: {task.source}
          </div>
        </div>
      </div>
    </div>
  );
}